
import React, { Component, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { reportError } from '@/lib/errorReporting';

interface Props {
  children: ReactNode;
  contextName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ContextErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    console.error(`ContextErrorBoundary caught an error in ${error.name}:`, error);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Context Error in ${this.props.contextName || 'Unknown'}:`, error, errorInfo);
    reportError(error, {
      source: 'client_boundary',
      severity: 'fatal',
      componentStack: errorInfo.componentStack ?? undefined,
      context: { boundary: 'ContextErrorBoundary', context_name: this.props.contextName ?? 'unknown' },
    });
  }

  render() {
    if (this.state.hasError) {
      const contextName = this.props.contextName || 'Application';
      
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl text-center flex items-center justify-center">
                <AlertTriangle className="text-orange-500 mr-2 h-6 w-6" />
                {contextName} Error
              </CardTitle>
              <CardDescription className="text-center">
                There was an issue initializing the {contextName.toLowerCase()} system
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4 pb-4 text-center">
              <p className="mb-4 text-sm text-muted-foreground">
                Please try refreshing the page. If the problem persists, contact support.
              </p>
              <Button 
                onClick={() => window.location.reload()}
                className="w-full"
              >
                Refresh Page
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
